import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EmittableKind, MembershipStage } from '@dosfilos/domain';

// ─── La confirmación, y por qué NO es la misma para los dos (R1-PR5) ────────
//
// Confirmar por confirmar entrena a apretar "sí". Estos dos diálogos existen
// porque los dos actos son distintos de verdad:
//
//  · CONSULTA: pedís comentarios. No cambia tu etapa, la red puede responder
//    cuando pueda, y podés retirarla. La confirmación es un recordatorio, no una
//    advertencia.
//  · SOLICITUD: pedís que TMAI te acepte. Si la aprueban, ARRANCA UN PLAZO
//    NORMATIVO DE DOS AÑOS que no se deshace desde la plataforma. Eso hay que
//    decirlo con todas las letras ANTES, no descubrirlo después.
//
// Y las dos nombran lo que queda registrado: una ronda nunca se borra — es el
// expediente de acreditación de la institución.

export function SubmitConfirmDialog({
  kind,
  stage,
  busy,
  onCancel,
  onConfirm,
}: {
  kind: EmittableKind;
  stage: MembershipStage;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('membership');
  const isFormal = kind === 'formal';

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onCancel}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t(isFormal ? 'network.confirmFormalTitle' : 'network.confirmAdvisoryTitle')}
      </DialogTitle>
      {busy && <LinearProgress />}
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            {t(isFormal ? 'network.confirmFormalBody' : 'network.confirmAdvisoryBody', {
              stage: t(`stages.${stage}`),
            })}
          </Typography>

          {isFormal && <Alert severity="warning">{t('network.confirmFormalClock')}</Alert>}

          {/* Lo que queda registrado, en los dos casos. */}
          <Alert severity="info" variant="outlined">
            {t('network.confirmRecorded')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none' }}>
          {t('network.confirmCancel')}
        </Button>
        <Button
          variant="contained"
          color={isFormal ? 'primary' : 'inherit'}
          onClick={onConfirm}
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          {t(isFormal ? 'network.confirmFormalCta' : 'network.confirmAdvisoryCta')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
